import type { AxiosResponse, AxiosError } from 'axios';
import axios from 'axios';
import { objectToQuery } from '@mx/util/object-to-query';
import {
  sharkFetch as neoSharkFetch,
  isNeoError,
  StatReportType,
  StatErrorLevel,
  StatErrorCategory,
} from '../neo';
import { env } from './env';
import { CommonError, CommonErrorLevel } from './error/common-error';
import { Cid, statHelper } from '../stat';
import { sleep } from './utils';
import { Runtime } from './enum-type';

interface AjaxSettings {
  useShark?: boolean;
}

interface CommonQueryParams {
  member_id?: string;
}

// test/dev 接口超时时间为 30s，st/prod 接口超时时间为 20s
const axiosTimeout = env.runtime < Runtime.Test ? 20000 : 30000;

export function post<T>(
  url: string,
  data: any,
  settings: AjaxSettings = {}
): Promise<T> {
  let ajaxRequest!: Promise<T>;
  let isSharkRequestFinished = false;
  let isReported = false;
  const bids = statHelper.getAjaxUrlBid(url);
  const startTimestamp = new Date().getTime();
  const reportSuccess = (res: any, useShark: boolean) => {
    const duration = new Date().getTime() - startTimestamp;
    if (!isReported && bids) {
      const lxTags: Record<string, string | number | boolean> = {
        duration,
      };
      if (bids.type === 'openoneclickpay') {
        lxTags.opened = res.data.opened;
      }
      statHelper.reportToLX(
        Cid.OneclickpayModal,
        bids.lx.start,
        lxTags,
        StatReportType.SC
      );
      statHelper.reportMetric(bids.cat.start, 1, {
        duration,
      });
      isReported = true;
    }
  };
  const reportFail = (error: any, useShark: boolean) => {
    const duration = new Date().getTime() - startTimestamp;
    if (!isReported && bids) {
      statHelper.reportToLX(
        Cid.OneclickpayModal,
        bids.lx.fail,
        {
          duration,
          error_code: error && error.code,
          error_msg: error && error.toString(),
        },
        StatReportType.SC
      );
      statHelper.reportMetric(bids.cat.fail, 1, {
        error_code: error && error.code,
      });
      isReported = true;
    }
  };
  const sendAjaxRequest = (): void => {
    ajaxRequest = ajaxPost<T>(url, data);
    ajaxRequest
      .then((res: T) => {
        reportSuccess(res, false);
      })
      .catch((err: any) => {
        reportFail(err, false);
      });
  };

  if (bids) {
    statHelper.reportToLX(
      Cid.OneclickpayModal,
      bids.lx.start,
      {},
      StatReportType.SC
    );
    statHelper.reportMetric(bids.cat.start, 1, {});
  }

  // 未使用 shark，直接发起 AJAX 请求， beam app里调不了shark桥
  if (!settings.useShark || env.commonParams.nb_app === 'beam') {
    sendAjaxRequest();
    return ajaxRequest;
  }

  const sharkRequest = neoSharkFetch<T>({
    host: window.location.origin,
    path: url,
    params: data,
  }).finally(() => {
    isSharkRequestFinished = true;
  });

  const delayAjaxRequest = sleep(2500).then(() => {
    if (!isSharkRequestFinished) {
      if (!ajaxRequest) {
        sendAjaxRequest();
      }
      return ajaxRequest;
    }
    return sharkRequest;
  });

  return Promise.race([sharkRequest, delayAjaxRequest])
    .then((res: T) => {
      reportSuccess(res, isSharkRequestFinished);
      return res;
    })
    .catch((err: any) => {
      // neoSharkFetch 错误立即发起 AJAX 请求
      if (isNeoError(err)) {
        if (!ajaxRequest) {
          sendAjaxRequest();
        }
        return ajaxRequest;
      }
      throw err;
    });
}

function ajaxPost<T>(
  url: string,
  data: any,
  settings: AjaxSettings = {}
): Promise<T> {
  const queryParams: CommonQueryParams = {};
  if (env.memberId) {
    queryParams.member_id = env.memberId;
  }
  let params = data;
  // beam app里参数隧道桥获取不到nb参数，由web获取
  if (env.commonParams.nb_app === 'beam') {
    params = {
      ...params,
      ...env.commonParams,
    };
  }
  const options = {
    // 通用 query 参数（SET 化参数）
    params: {
      ...env.requestQueryParams,
      ...queryParams,
    },
    transformRequest: (rawData: any) => {
      try {
        return objectToQuery({ ...rawData });
      } catch (err) {
        statHelper.reportToRaptor(
          'ApiParseError',
          {
            request_path: url,
            error_code: 'parse',
            message: err?.message,
          },
          StatErrorLevel.Error,
          StatErrorCategory.AJAXError
        );
      }
      return '';
    },
    timeout: axiosTimeout,
  };
  // const requestStartTime = new Date().getTime();

  // statHelper.reportToLX(AjaxBids.AjaxStart, {
  //   request_path: url,
  // }, StatReportType.SC);
  // statHelper.reportMetric(RaptorMetricKeys.AjaxRequestStart, 1, {
  //   request_path: url,
  // });

  return axios
    .post<T>(url, params, options)
    .then((rsp: AxiosResponse<any>) => {
      const rspData = rsp.data;

      if (!rspData) {
        throw new CommonError('0', CommonErrorLevel.TOAST, '返回值异常');
      }

      // statHelper.reportToLX(AjaxBids.AjaxSuccess, {
      //   request_path: url,
      //   request_time: requestTime,
      // }, StatReportType.SC);
      // statHelper.reportMetric(RaptorMetricKeys.AjaxRequestSuccess, requestTime, {
      //   request_path: url,
      // });

      return Promise.resolve(rspData);
    })
    .catch((error: AxiosError | CommonError) => {
      let errorObj = {};
      // let statErrorType = 'catch';

      if (error) {
        const { config, ...restError } = error as AxiosError;
        errorObj = restError;
      }
      // if (CommonError.isCommonError(error)) {
      //   statErrorType = 'data_empty';
      // }

      // statHelper.reportToLX(AjaxBids.AjaxFail, {
      //   request_path: url,
      //   request_time: requestTime,
      //   error_type: statErrorType,
      //   error: errorObj,
      // }, StatReportType.SC);
      statHelper.reportToRaptor(
        'ApiErrorCode',
        {
          request_path: url,
          error_code: error && error.code,
          message: error && error.toString(),
          error: errorObj,
        },
        StatErrorLevel.Error,
        StatErrorCategory.AJAXError
      );
      // statHelper.reportMetric(RaptorMetricKeys.AjaxRequestFail, requestTime, {
      //   request_path: url,
      //   error_type: statErrorType,
      //   message: error && error.toString(),
      // });
      return Promise.reject(error);
    });
}
